import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel } from 'react-bootstrap';
import { navigate } from 'raviger';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';
import Permission from '../../components/Permission/Permission';

import { useAlertStore } from '../../store/providers/Alert';
import { useRealEstateBoardStore } from '../../store/providers/RealEstateBoard';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import { columns, sorting } from './RealEstateBoardListSchema';

const BaseList = React.lazy(() => import('../../components/Lists/BaseList/BaseList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));

function RealEstateBoards() {
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { listRecords, deleteRecord, data, clearData } = useRealEstateBoardStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);

    const pagePermission = constants.PERMISSION_SETTINGS_REALESTATEBOARDS;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const selectedHandler = (realEstateBoard) => {
        navigate(`/realEstateBoards/edit/${realEstateBoard.id}`);
    };

    const addedHandler = () => {
        navigate('/realEstateBoards/new');
    };

    const deletedHandler = (realEstateBoard) => {
        deleteRecord({ realEstateBoardId: realEstateBoard.id });
    };

    const refreshedHandler = () => {
        listRecords();
    };

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {
                setListData(data.body);
            }

            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, listRecords]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'realEstateBoards', parent: 'Settings', text: 'Real Estate Boards', path: window.location.pathname }));
        listRecords();

        setIsMounted(true);
    }, [isMounted, hasPermission, listRecords, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    return (
        <>
            <SideMenu activeKey="settings" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Real Estate Boards" />
                            <Panel>
                                <Panel.Body>
                                    <BaseList
                                        compType="Real Estate Board"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        permissionEdit={canEdit}
                                        hideCopy={true}
                                        hidePrint={true}
                                        hideFilter={true}
                                        added={() => addedHandler()}
                                        selected={(realEstateBoard) => selectedHandler(realEstateBoard)}
                                        deleted={(realEstateBoard) => deletedHandler(realEstateBoard)}
                                        refreshed={() => refreshedHandler()} />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(RealEstateBoards));